/**
 * Comprehensive tour validation utilities
 * Provides centralized validation logic for tour names and guest extraction
 */

import { isSeatPackage } from './tourScoring';

export interface ValidationResult {
  valid: boolean;
  reason?: string;
}

export interface GuestInfo {
  guestName: string;
  role: 'guest' | 'opener' | 'featured';
}

/**
 * Patterns that indicate a tour name is actually a guest-only descriptor
 * These should be filtered out as separate tours but the guest info should be captured
 */
const GUEST_ONLY_PATTERNS = [
  /^(w\/|with)\s+(.+)/i,
  /^(feat\.?|featuring)\s+(.+)/i,
  /^(\+)\s+(.+)/i,
  /^(special\s+guest[s]?:?\s*)(.+)/i,
  /^(support[ed]?\s+by:?\s*)(.+)/i,
  /^(opening\s+act:?\s*)(.+)/i,
];

/**
 * Patterns that indicate invalid tour name starts
 */
const INVALID_START_PATTERNS = [
  /^[|,+/\-:*&]/, // Starts with punctuation
  /^\d{4}\s*[-:]?\s*$/i, // Year only (e.g., "2026", "2025:")
  /^\d{4}\s+(tour|europe|usa|uk|world)/i, // Year + region (e.g., "2026 EUROPE")
  /^\(\d+\+\s*event\)$/i, // Age restriction only
];

/**
 * Patterns for ticket/hotel packages that should never be saved as tours
 */
const TICKET_PACKAGE_PATTERNS = [
  /ticket\s*\+\s*hotel/i,
  /hotel\s*\+\s*ticket/i,
  /ticket.*hotel/i,
  /hotel.*ticket/i,
  /official\s*ticket/i,
  /\bbundles?\b/i,
  /\bupgrade\b/i,
  /niet\s*inbegrepen/i, // Dutch "not included"
  /not\s*included/i,
  // Festival admission products (frequently returned as "tours")
  /^abono\b/i, // Spanish festival pass ("abono general 3 días ...")
  /^individual\s+(general|comfort|banamex|abono)\b/i,
  /\bcomfort\s*pass\b/i,
  /\bgeneral\s*admission\b/i,
  /\b\d+\s*d[ií]as?\b/i, // "3 días"
  /\bfestival\b[^a-z]*.*\b(friday|saturday|sunday|thursday|day\s*\d)\b/i, // single festival day
];

/**
 * Patterns for venue areas/rooms that shouldn't be tour names
 */
const VENUE_AREA_PATTERNS = [
  /\bfunction\s*room\b/i,
  /\borangery\b/i,
  /\bplaza\s*de\b/i,
  /\blounge\b/i,
  /\bterrace\b/i,
  /\bgarden\s*area\b/i,
  /\bfortnum\b/i,
  /\bmason\b/i,
  /\bbox\s*seat/i,
  /\bcomfort\s*seats?\b/i,
  /\breserved\s*seat/i,
  /\bbusiness\s*seat/i,
  /\b\d+[-\s]*day\s*ticket\b/i,   // "2-day ticket" OR "2 Day Ticket"
  /\b\d+[-\s]*day\s*pass\b/i,     // "3 Day Pass"
  /^\d+(st|nd|rd|th)\s+show$/i,   // "2nd Show" - too generic
  /^\d+(st|nd|rd|th)\s+night$/i,  // "2nd Night"
  /^night\s*\d+$/i,               // "Night 2"
];

/**
 * Validates if a tour name is legitimate and should be saved to the database
 * Returns false for invalid names that should be filtered out
 */
export const isValidTourName = (name: string): ValidationResult => {
  if (!name || name.trim().length < 3) {
    return { valid: false, reason: 'too_short' };
  }

  const trimmed = name.trim();

  // 1. Check for invalid start characters
  if (INVALID_START_PATTERNS.some(p => p.test(trimmed))) {
    return { valid: false, reason: 'invalid_start' };
  }

  // 2. Check if it's a guest-only descriptor (not a real tour)
  if (GUEST_ONLY_PATTERNS.some(p => p.test(trimmed))) {
    return { valid: false, reason: 'guest_only_descriptor' };
  }

  // 3. Check for ticket/hotel packages
  if (TICKET_PACKAGE_PATTERNS.some(p => p.test(trimmed))) {
    return { valid: false, reason: 'ticket_package' };
  }

  // 4. Check for venue areas
  if (VENUE_AREA_PATTERNS.some(p => p.test(trimmed))) {
    return { valid: false, reason: 'venue_area' };
  }

  // 5. Check seat packages (delegate to existing function)
  if (isSeatPackage(trimmed)) {
    return { valid: false, reason: 'seat_package' };
  }

  // 6. Check that the name has sufficient alphabetic content
  const alphaOnly = trimmed.replace(/[^a-z]/gi, '');
  if (alphaOnly.length < 5) {
    return { valid: false, reason: 'insufficient_content' };
  }

  // 7. Check for age restriction-only entries
  if (/^\d{1,2}\s*(and\s*under|and\s*over|\+)/i.test(trimmed)) {
    return { valid: false, reason: 'age_restriction_only' };
  }

  return { valid: true };
};

/**
 * Extracts guest/opener artist information from a tour name
 * Returns the guest name and role if found, or null if no guest pattern detected
 */
export const extractGuestInfo = (tourName: string): GuestInfo | null => {
  if (!tourName) return null;

  const trimmed = tourName.trim();

  // Check each guest pattern and extract the artist name
  for (const pattern of GUEST_ONLY_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match && match[2]) {
      const guestName = match[2].trim();
      
      // Determine role based on pattern
      let role: 'guest' | 'opener' | 'featured' = 'guest';
      if (/feat|featuring/i.test(match[1])) {
        role = 'featured';
      } else if (/opening|support/i.test(match[1])) {
        role = 'opener';
      }

      // Clean up the guest name
      const cleanedName = guestName
        .replace(/[,&]\s*$/, '') // Remove trailing comma or ampersand
        .replace(/\s*\([^)]*\)$/, '') // Remove parenthetical content
        .trim();

      if (cleanedName.length >= 2) {
        return { guestName: cleanedName, role };
      }
    }
  }

  // Also check for "& [Artist]" at the end of tour names
  const ampersandMatch = trimmed.match(/\s*&\s+([A-Z][a-zA-Z\s]+)$/);
  if (ampersandMatch && ampersandMatch[1]) {
    const guestName = ampersandMatch[1].trim();
    // Exclude band names that naturally have "& the" in them
    if (!/^the\s/i.test(guestName) && guestName.length >= 2) {
      return { guestName, role: 'guest' };
    }
  }

  return null;
};

/**
 * Pre-filters an event name before grouping
 * Returns false if the event should be completely excluded from processing
 */
export const shouldExcludeEvent = (eventName: string): boolean => {
  if (!eventName) return true;

  const trimmed = eventName.trim();

  // Exclude events starting with pipe (package variants)
  if (/^\|/.test(trimmed)) return true;

  // Exclude guest-only descriptors
  if (GUEST_ONLY_PATTERNS.some(p => p.test(trimmed))) return true;

  // Exclude very short names (likely just ticket types)
  const alphaOnly = trimmed.replace(/[^a-z]/gi, '');
  if (alphaOnly.length < 5) return true;

  // Exclude obvious ticket packages
  if (TICKET_PACKAGE_PATTERNS.some(p => p.test(trimmed))) return true;

  return false;
};

/**
 * Combined validation for use in deduplication
 * Returns true if the tour should be kept, false if it should be filtered out
 */
export const isValidTourForDisplay = (name: string): boolean => {
  const result = isValidTourName(name);
  return result.valid;
};
